import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import {
  Box,
  AppBar,
  Typography,
  Slider,
  Tooltip,
  CircularProgress,
  TextField,
  Divider,
  MenuItem,
  FormControl,
  FormControlLabel,
  FormLabel,
  Drawer,
  List,
  ListItem,
  ListItemText,
  Radio,
  RadioGroup,
  Checkbox,
} from "@mui/material";

import { IconButton, Button, Sheet } from "@mui/joy";

import FileOpenIcon from "@mui/icons-material/FileOpen";
import AlignHorizontalLeftIcon from "@mui/icons-material/AlignHorizontalLeft";
import AlignHorizontalCenterIcon from "@mui/icons-material/AlignHorizontalCenter";
import AlignHorizontalRightIcon from "@mui/icons-material/AlignHorizontalRight";
import VerticalAlignTopIcon from "@mui/icons-material/VerticalAlignTop";
import VerticalAlignCenterIcon from "@mui/icons-material/VerticalAlignCenter";
import VerticalAlignBottomIcon from "@mui/icons-material/VerticalAlignBottom";
import ArrowUpwardIcon from "@mui/icons-material/ArrowUpward";
import ArrowDownwardIcon from "@mui/icons-material/ArrowDownward";
import { PDFDocument } from "pdf-lib";

// pdfjs-dist types can be a bit loose; import as any to avoid bundler friction
// eslint-disable-next-line @typescript-eslint/ban-ts-comment
// @ts-ignore
import * as pdfjsLib from "pdfjs-dist";
import { GlobalWorkerOptions } from "pdfjs-dist";

// If you use TypeScript, keep the ignore to satisfy Vite's worker import typing.
// @ts-ignore
import PdfWorker from "pdfjs-dist/build/pdf.worker.min.mjs?worker";
import { useParams } from "react-router-dom";
import { useForm } from "src/redux/form/formHooks";
import { useUpdateFormMutation } from "src/redux/form/formEndpoints";

// Tell pdf.js to use this dedicated worker instance
GlobalWorkerOptions.workerPort = new PdfWorker();

/**
 * Modes for the interaction layer
 */
type Mode = "pan" | "draw" | "edit";

/**
 * ViewportInfo describes how a given PDF page maps between
 * PDF points (72 dpi, bottom-left origin) and CSS pixels (top-left origin)
 */
type ViewportInfo = {
  widthPx: number;
  heightPx: number;
  scale: number; // pdf.js scale used
  widthPt: number; // pdf-lib page width (points)
  heightPt: number; // pdf-lib page height (points)
};

/**
 * We store geometry canonically in PDF points so it is zoom-invariant.
 */
type Field = {
  id: string;
  pageIndex: number; // 0-based
  xPt: number; // PDF points, bottom-left origin
  yPt: number; // PDF points
  wPt: number; // PDF points
  hPt: number; // PDF points
  value: string;
  name: string; // editable display/name used on export
  group?: string; // optional grouping label
  excludeFromResync: boolean;
  type: "text" | "date" | "checkbox";
  textAlign: "left" | "center" | "right"; // new: text alignment for the field's content
  selected?: boolean;
};

const clamp = (v: number, min: number, max: number) =>
  Math.max(min, Math.min(max, v));

const Handle: React.FC<{
  x: number;
  y: number;
  onMouseDown: (e: React.MouseEvent) => void;
}> = ({ x, y, onMouseDown }) => {
  return (
    <div
      onMouseDown={onMouseDown}
      style={{
        position: "absolute",
        left: x - 5,
        top: y - 5,
        width: 10,
        height: 10,
        background: "white",
        border: "2px solid #1976d2",
        borderRadius: 2,
        boxSizing: "border-box",
        cursor: "nwse-resize",
        zIndex: 3,
      }}
    />
  );
};

/**
 * Main component: PDF textfield annotator
 * - Load a PDF
 * - Render the current page with pdf.js
 * - Overlay interactive boxes representing text fields
 * - Export a new PDF with AcroForm text fields placed via pdf-lib
 */
const PdfTextfieldAnnotator: React.FC = () => {
  const params = useParams();
  const id = params.id || "";
  const formId = Number(id);
  const { form, isLoading: isFormLoading } = useForm(formId, {
    skip: !formId,
  });
  const [updateForm, { isLoading: isSaving }] = useUpdateFormMutation();

  const canvasRef = useRef<HTMLCanvasElement | null>(null);
  const overlayRef = useRef<HTMLDivElement | null>(null);
  const containerRef = useRef<HTMLDivElement | null>(null);

  const [fileName, setFileName] = useState<string>("");
  const [pdfBytes, setPdfBytes] = useState<Uint8Array | null>(null);
  const [loading, setLoading] = useState(false);
  const [pdfDocProxy, setPdfDocProxy] = useState<any | null>(null);
  const [currentPage, setCurrentPage] = useState(0); // 0-based
  const [numPages, setNumPages] = useState(0);
  const [zoom, setZoom] = useState(1.0);
  const [mode, setMode] = useState<Mode>("draw");
  const [fields, setFields] = useState<Field[]>([]);
  const [lastCreatedId, setLastCreatedId] = useState<string | null>(null);
  const pdfBytesRef = useRef<Uint8Array | null>(null);
  const [viewportVersion, setViewportVersion] = useState(0);
  const [draggingId, setDraggingId] = useState<string | null>(null);
  const dragGroupRef = useRef<{
    startX: number;
    startY: number;
    items: {
      id: string;
      xPt: number;
      yPt: number;
      pageIndex: number;
      wPt: number;
      hPt: number;
    }[];
  } | null>(null);
  // Clipboard for copy/paste of fields
  const clipboardRef = useRef<Field[] | null>(null);
  const [resizing, setResizing] = useState<{
    id: string;
    corner: "se" | "nw";
  } | null>(null);
  const [drawingRect, setDrawingRect] = useState<{
    startX: number;
    startY: number;
    x: number;
    y: number;
    w: number;
    h: number;
  } | null>(null);
  const [isFieldListOpen, setFieldListOpen] = useState(false);
  const [selectionRect, setSelectionRect] = useState<{
    startX: number;
    startY: number;
    x: number;
    y: number;
    w: number;
    h: number;
  } | null>(null);
  const listItemRefs = useRef<Record<string, HTMLDivElement | null>>({});

  // cache for page viewport mapping (px <-> pt)
  const viewportMap = useRef<Record<number, ViewportInfo>>({});

  const applyConfigPayload = useCallback((payload: any) => {
    if (!payload || !Array.isArray(payload.fields)) {
      throw new Error("Invalid JSON: missing 'fields' array.");
    }

    // Sanitize and normalize fields; ensure no 'selected' state is persisted
    const sanitized: Field[] = payload.fields
      .filter((f: any) => f && typeof f.pageIndex === "number")
      .map(
        (f: any): Field => ({
          id: typeof f.id === "string" ? f.id : crypto.randomUUID(),
          pageIndex: Number(f.pageIndex) || 0,
          xPt: Number(f.xPt) || 0,
          yPt: Number(f.yPt) || 0,
          wPt: Math.max(1, Number(f.wPt) || 0),
          hPt: Math.max(1, Number(f.hPt) || 0),
          value: typeof f.value === "string" ? f.value : "",
          name: typeof f.name === "string" ? f.name : "Field",
          group: typeof f.group === "string" ? f.group : "",
          excludeFromResync:
            typeof f.excludeFromResync === "boolean"
              ? f.excludeFromResync
              : false,
          type: f.type === "date" || f.type === "checkbox" ? f.type : "text",
          textAlign:
            typeof f.textAlign === "string" &&
            ["left", "center", "right"].includes(f.textAlign)
              ? (f.textAlign as "left" | "center" | "right")
              : "left",
          selected: false,
        })
      );

    // Optionally clamp to existing page sizes if available
    const vpMap = viewportMap.current;
    const clamped = sanitized.map((ff): Field => {
      const vp = vpMap[ff.pageIndex];
      if (!vp) return ff;
      const maxX = vp.widthPt - ff.wPt;
      const maxY = vp.heightPt - ff.hPt;
      return {
        ...ff,
        xPt: clamp(ff.xPt, 0, maxX),
        yPt: clamp(ff.yPt, 0, maxY),
      };
    });

    setFields(clamped);

    // If there is a zoom value in the JSON, use it—purely optional
    if (
      typeof payload.zoomAtExport === "number" &&
      isFinite(payload.zoomAtExport)
    ) {
      setZoom(payload.zoomAtExport);
    }

    // If there is a source name, keep it around (non-blocking)
    if (
      typeof payload.sourcePdfBaseName === "string" &&
      payload.sourcePdfBaseName.trim()
    ) {
      setFileName(payload.sourcePdfBaseName.trim());
    }
  }, []);

  /**
   * Render a page to canvas and cache the px<->pt mapping
   */
  const renderPage = useCallback(
    async (
      doc: any,
      pageIndex: number,
      scale: number,
      rawBytes?: Uint8Array
    ) => {
      const page = await doc.getPage(pageIndex + 1);
      const viewport = page.getViewport({ scale });
      const canvas = canvasRef.current!;
      const ctx = canvas.getContext("2d")!;
      canvas.width = Math.floor(viewport.width);
      canvas.height = Math.floor(viewport.height);
      const renderContext = { canvasContext: ctx, viewport };
      await page.render(renderContext).promise;

      // Map to PDF point size using pdf-lib for the same page
      const bytes = rawBytes || pdfBytesRef.current;
      if (bytes) {
        const libDoc = await PDFDocument.load(bytes);
        const libPage = libDoc.getPage(pageIndex);
        const { width, height } = libPage.getSize(); // points
        viewportMap.current[pageIndex] = {
          widthPx: viewport.width,
          heightPx: viewport.height,
          scale,
          widthPt: width,
          heightPt: height,
        };
        setViewportVersion((v) => v + 1); // trigger re-render so overlay picks up viewport size
      }
    },
    []
  );

  /**
   * Load PDF with pdf.js and render first page to populate viewport info
   */
  const loadPdf = useCallback(
    async (bytes: Uint8Array) => {
      pdfBytesRef.current = bytes;
      setLoading(true);
      try {
        const loadingTask = pdfjsLib.getDocument({ data: [...bytes] });
        const doc = await loadingTask.promise;
        setPdfDocProxy(doc);
        setNumPages(doc.numPages);
        setCurrentPage(0);
        await renderPage(doc, 0, zoom, bytes);
      } finally {
        setLoading(false);
      }
    },
    [renderPage, zoom]
  );

  // Re-render page on zoom/page changes
  useEffect(() => {
    (async () => {
      if (pdfDocProxy) {
        setLoading(true);
        try {
          await renderPage(pdfDocProxy, currentPage, zoom);
        } finally {
          setLoading(false);
        }
      }
    })();
  }, [currentPage, zoom, pdfDocProxy, renderPage]);

  // Sync overlay size to canvas
  const overlayStyle = useMemo(() => {
    const vp = viewportMap.current[currentPage];
    return {
      width: vp ? vp.widthPx : 0,
      height: vp ? vp.heightPx : 0,
    };
  }, [
    currentPage,
    viewportMap.current[currentPage]?.widthPx,
    viewportMap.current[currentPage]?.heightPx,
    viewportVersion,
  ]);

  useEffect(() => {
    const url = form?.url;
    if (!url) return;
    let cancelled = false;

    (async () => {
      try {
        const response = await fetch(url);
        if (!response.ok) {
          throw new Error(`Failed to fetch PDF (${response.status})`);
        }
        const buffer = await response.arrayBuffer();
        if (cancelled) return;
        const bytes = new Uint8Array(buffer);
        setFileName(form?.name || "document");
        setPdfBytes(bytes);
        await loadPdf(bytes);
      } catch (e) {
        console.error("Failed to load PDF from API", e);
      }
    })();

    return () => {
      cancelled = true;
    };
    // loadPdf is stable; avoid re-fetch on state changes to prevent loops
  }, [form?.name, form?.url]);

  /**
   * Import fields from a JSON file (same structure as export)
   */
  const importJson = async (file: File) => {
    try {
      const text = await file.text();
      const payload = JSON.parse(text);
      applyConfigPayload(payload);
    } catch (e) {
      console.error(e);
      alert(
        "Failed to import fields JSON. Make sure it matches the exported structure."
      );
    }
  };

  useEffect(() => {
    if (!form?.config) return;
    try {
      const parsed =
        typeof form.config === "string" ? JSON.parse(form.config) : form.config;
      applyConfigPayload(parsed);
    } catch (e) {
      console.error("Failed to load form config from API", e);
    }
  }, [applyConfigPayload, form?.config]);

  /**
   * Convert CSS pixel rect (top-left origin) to PDF points (bottom-left origin)
   */
  const toPdfPoints = (
    pageIndex: number,
    xPx: number,
    yPx: number,
    wPx: number,
    hPx: number
  ) => {
    const vp = viewportMap.current[pageIndex];
    if (!vp) return { x: 0, y: 0, w: 0, h: 0 };
    const kx = vp.widthPx / vp.widthPt; // px per pt
    const ky = vp.heightPx / vp.heightPt;
    const w = wPx / kx;
    const h = hPx / ky;
    const x = xPx / kx;
    const yTopPx = yPx; // from top
    const y = (vp.heightPx - (yTopPx + hPx)) / ky; // bottom-left origin
    return { x, y, w, h };
  };

  /**
   * Inverse: convert PDF points to CSS pixel rect for current viewport
   */
  const fromPdfPoints = (
    pageIndex: number,
    xPt: number,
    yPt: number,
    wPt: number,
    hPt: number
  ) => {
    const vp = viewportMap.current[pageIndex];
    if (!vp) return { x: 0, y: 0, w: 0, h: 0 };
    const kx = vp.widthPx / vp.widthPt;
    const ky = vp.heightPx / vp.heightPt;
    const x = xPt * kx;
    const w = wPt * kx;
    const h = hPt * ky;
    const yTopPx = vp.heightPx - (yPt + hPt) * ky;
    return { x, y: yTopPx, w, h };
  };

  /** Drawing new field **/
  const beginDraw = (e: React.MouseEvent) => {
    if (mode !== "draw") return;
    const rect = overlayRef.current!.getBoundingClientRect();
    const startX = e.clientX - rect.left;
    const startY = e.clientY - rect.top;
    setDrawingRect({ startX, startY, x: startX, y: startY, w: 0, h: 0 });
  };

  const onDrawMove = (e: React.MouseEvent) => {
    if (!drawingRect) return;
    const rect = overlayRef.current!.getBoundingClientRect();
    const currX = e.clientX - rect.left;
    const currY = e.clientY - rect.top;
    const x = Math.min(currX, drawingRect.startX);
    const y = Math.min(currY, drawingRect.startY);
    const w = Math.abs(currX - drawingRect.startX);
    const h = Math.abs(currY - drawingRect.startY);
    setDrawingRect({ ...drawingRect, x, y, w, h });
  };

  const endDraw = () => {
    if (!drawingRect) return;
    if (drawingRect.w > 8 && drawingRect.h > 8) {
      const id = crypto.randomUUID();
      const { x, y, w, h } = drawingRect;
      const {
        x: xPt,
        y: yPt,
        w: wPt,
        h: hPt,
      } = toPdfPoints(currentPage, x, y, w, h);
      setFields((prev) => {
        const cleared = prev.map((ff) => ({ ...ff, selected: false }));
        return cleared.concat({
          id,
          pageIndex: currentPage,
          xPt,
          yPt,
          wPt,
          hPt,
          value: "",
          name: `Field ${prev.length + 1}`,
          group: "",
          excludeFromResync: false,
          type: "text",
          textAlign: "left",
          selected: true,
        });
      });
      setLastCreatedId(id);
    }
    setDrawingRect(null);
  };

  const beginMarquee = (e: React.MouseEvent) => {
    if (mode !== "edit") return;
    const rect = overlayRef.current!.getBoundingClientRect();
    const startX = e.clientX - rect.left;
    const startY = e.clientY - rect.top;
    setSelectionRect({ startX, startY, x: startX, y: startY, w: 0, h: 0 });
  };

  const onMarqueeMove = (e: React.MouseEvent) => {
    if (!selectionRect) return;
    const rect = overlayRef.current!.getBoundingClientRect();
    const currX = e.clientX - rect.left;
    const currY = e.clientY - rect.top;
    const x = Math.min(currX, selectionRect.startX);
    const y = Math.min(currY, selectionRect.startY);
    const w = Math.abs(currX - selectionRect.startX);
    const h = Math.abs(currY - selectionRect.startY);
    const nextRect = { ...selectionRect, x, y, w, h };
    setSelectionRect(nextRect);
    updateSelectionFromRect(nextRect);
  };

  const endMarquee = () => {
    if (selectionRect) {
      if (selectionRect.w < 4 && selectionRect.h < 4) {
        // treat as click-away deselect if tiny
        setFields((prev) => prev.map((ff) => ({ ...ff, selected: false })));
      }
    }
    setSelectionRect(null);
  };

  /** Marquee selection **/
  const updateSelectionFromRect = useCallback(
    (rect: { x: number; y: number; w: number; h: number }) => {
      const x2 = rect.x + rect.w;
      const y2 = rect.y + rect.h;
      setFields((prev) =>
        prev.map((ff) => {
          if (ff.pageIndex !== currentPage) return { ...ff, selected: false };
          const view = fromPdfPoints(
            ff.pageIndex,
            ff.xPt,
            ff.yPt,
            ff.wPt,
            ff.hPt
          );
          const overlaps =
            view.x < x2 &&
            view.x + view.w > rect.x &&
            view.y < y2 &&
            view.y + view.h > rect.y;
          return { ...ff, selected: overlaps };
        })
      );
    },
    [currentPage]
  );

  /** Dragging **/
  const startDrag = (e: React.MouseEvent, id: string) => {
    if (mode === "pan") return;
    e.stopPropagation();

    // Determine next selection (support Cmd/Ctrl to toggle)
    const wasSelected = fields.find((ff) => ff.id === id)?.selected;
    let nextSelectedIds = new Set(
      fields.filter((ff) => ff.selected).map((ff) => ff.id)
    );

    if (e.metaKey || e.ctrlKey) {
      // Do not unselect anything when command/control is pressed; only add the clicked item if it wasn't selected
      if (!wasSelected) {
        nextSelectedIds.add(id);
      }
    } else {
      // Without modifiers, select only the clicked item
      nextSelectedIds = new Set([id]);
    }

    // Apply selection
    setFields((prev) =>
      prev.map((ff) => ({ ...ff, selected: nextSelectedIds.has(ff.id) }))
    );

    // If clicked item ended up unselected (toggled off), don't start a drag
    if (!nextSelectedIds.has(id)) return;

    setDraggingId(id);

    const rect = overlayRef.current!.getBoundingClientRect();
    const startX = e.clientX - rect.left;
    const startY = e.clientY - rect.top;

    // Snapshot selected items on the current page for grouped dragging
    const items = fields
      .filter(
        (ff) => nextSelectedIds.has(ff.id) && ff.pageIndex === currentPage
      )
      .map((ff) => ({
        id: ff.id,
        xPt: ff.xPt,
        yPt: ff.yPt,
        pageIndex: ff.pageIndex,
        wPt: ff.wPt,
        hPt: ff.hPt,
      }));

    dragGroupRef.current = { startX, startY, items };
  };

  const onDragMove = (e: React.MouseEvent) => {
    if (!draggingId || !dragGroupRef.current) return;
    const rect = overlayRef.current!.getBoundingClientRect();
    const currX = e.clientX - rect.left;
    const currY = e.clientY - rect.top;

    const { startX, startY, items } = dragGroupRef.current;
    const dxPx = currX - startX;
    const dyPx = currY - startY;

    const vp = viewportMap.current[currentPage];
    if (!vp) return;

    // Convert pixel delta to PDF point delta (note Y inversion)
    const dxPt = dxPx / (vp.widthPx / vp.widthPt);
    const dyPt = -dyPx / (vp.heightPx / vp.heightPt);

    setFields((prev) =>
      prev.map((ff) => {
        const snap = items.find((it) => it.id === ff.id);
        if (!snap) return ff;
        let xPt = snap.xPt + dxPt;
        let yPt = snap.yPt + dyPt;

        const pageVp = viewportMap.current[ff.pageIndex];
        if (pageVp) {
          const maxX = pageVp.widthPt - ff.wPt;
          const maxY = pageVp.heightPt - ff.hPt;
          xPt = clamp(xPt, 0, maxX);
          yPt = clamp(yPt, 0, maxY);
        }
        return { ...ff, xPt, yPt };
      })
    );
  };

  const endDrag = () => {
    setDraggingId(null);
    dragGroupRef.current = null;
  };

  /** Resizing **/
  const [resizeCache, setResizeCache] = useState<any>(null);

  const startResize = (
    e: React.MouseEvent,
    id: string,
    corner: "se" | "nw"
  ) => {
    e.stopPropagation();
    setResizing({ id, corner });

    const rect = overlayRef.current!.getBoundingClientRect();
    const startX = e.clientX - rect.left;
    const startY = e.clientY - rect.top;

    const f = fields.find((ff) => ff.id === id)!;
    const startPx = fromPdfPoints(f.pageIndex, f.xPt, f.yPt, f.wPt, f.hPt);

    setResizeCache({ _startX: startX, _startY: startY, _startPx: startPx });
  };

  const onResizeMove = (e: React.MouseEvent) => {
    if (!resizing || !resizeCache) return;
    const f = fields.find((ff) => ff.id === resizing.id)!;

    const rect = overlayRef.current!.getBoundingClientRect();
    const currX = e.clientX - rect.left;
    const currY = e.clientY - rect.top;

    const { x, y, w, h } = resizeCache._startPx as {
      x: number;
      y: number;
      w: number;
      h: number;
    };
    const startX = resizeCache._startX as number;
    const startY = resizeCache._startY as number;

    const vp = viewportMap.current[f.pageIndex];

    let nx = x,
      ny = y,
      nw = w,
      nh = h;

    if (resizing.corner === "se") {
      nw = clamp(w + (currX - startX), 20, (vp?.widthPx ?? 0) - x);
      nh = clamp(h + (currY - startY), 20, (vp?.heightPx ?? 0) - y);
    } else {
      const maxX = x + w - 20;
      const maxY = y + h - 20;
      nx = clamp(currX, 0, maxX);
      ny = clamp(currY, 0, maxY);
      nw = w + (x - nx);
      nh = h + (y - ny);
    }

    const newPts = toPdfPoints(f.pageIndex, nx, ny, nw, nh);
    f.xPt = newPts.x;
    f.yPt = newPts.y;
    f.wPt = newPts.w;
    f.hPt = newPts.h;
    setFields([...fields]);
  };

  const endResize = () => setResizing(null);

  /** Utilities **/
  const deleteSelected = () =>
    setFields((prev) => prev.filter((f) => !f.selected));

  // Duplicate selected fields on current page, offset and clamp, select only new
  const duplicateSelected = useCallback(
    () =>
      setFields((prev) => {
        const vp = viewportMap.current[currentPage];
        const dxPt = vp ? 10 / (vp.widthPx / vp.widthPt) : 0; // 10px in points
        const dyPt = vp ? 10 / (vp.heightPx / vp.heightPt) : 0; // 10px in points

        const selectedOnPage = prev.filter(
          (f) => f.selected && f.pageIndex === currentPage
        );
        if (!selectedOnPage.length) return prev;

        // Optional usability: set clipboard to duplicated items
        clipboardRef.current = selectedOnPage.map((f) => ({ ...f }));

        const rest: Field[] = prev.map((ff) => ({ ...ff, selected: false }));

        const newOnes = selectedOnPage.map<Field>((f) => {
          const pageVp = viewportMap.current[f.pageIndex];
          const maxX = (pageVp?.widthPt ?? Infinity) - f.wPt;
          const maxY = (pageVp?.heightPt ?? Infinity) - f.hPt;
          const nx = clamp(f.xPt + dxPt, 0, maxX);
          const ny = clamp(f.yPt, 0, maxY); // keep baseline; adjust if diagonal desired
          return {
            ...f,
            id: crypto.randomUUID(),
            xPt: nx,
            yPt: ny,
            selected: true,
          };
        });

        return rest.concat(newOnes);
      }),
    [currentPage]
  );

  // Copy selected fields (current page) to clipboard
  const copySelected = useCallback(() => {
    setFields((prev) => {
      const copied = prev.filter(
        (f) => f.selected && f.pageIndex === currentPage
      );
      clipboardRef.current = copied.map((f) => ({ ...f }));
      return prev;
    });
  }, [currentPage]);

  // Paste clipboard fields onto current page, offset, select new ones
  const pasteClipboard = useCallback(() => {
    const clip = clipboardRef.current;
    if (!clip || !clip.length) return;

    setFields((prev) => {
      const vp = viewportMap.current[currentPage];
      const dxPt = vp ? 10 / (vp.widthPx / vp.widthPt) : 0;
      const dyPt = vp ? 10 / (vp.heightPx / vp.heightPt) : 0;

      const rest: Field[] = prev.map((ff) => ({ ...ff, selected: false }));

      // Paste onto the current page; keep geometry, offset by 10px
      const newOnes = clip
        .filter((f) => f.pageIndex === currentPage)
        .map<Field>((f) => {
          const pageVp = viewportMap.current[currentPage];
          const maxX = (pageVp?.widthPt ?? Infinity) - f.wPt;
          const maxY = (pageVp?.heightPt ?? Infinity) - f.hPt;
          const nx = clamp(f.xPt + dxPt, 0, maxX);
          const ny = clamp(f.yPt - dyPt, 0, maxY);
          return {
            ...f,
            id: crypto.randomUUID(),
            pageIndex: currentPage,
            xPt: nx,
            yPt: ny,
            selected: true,
          };
        });

      // If clipboard items were from another page or none matched, fall back to duplicating current selection
      const finalNewOnes: Field[] = newOnes.length
        ? newOnes
        : prev
            .filter((f) => f.selected && f.pageIndex === currentPage)
            .map<Field>((f) => {
              const pageVp = viewportMap.current[currentPage];
              const maxX = (pageVp?.widthPt ?? Infinity) - f.wPt;
              const maxY = (pageVp?.heightPt ?? Infinity) - f.hPt;
              const nx = clamp(f.xPt + dxPt, 0, maxX);
              const ny = clamp(f.yPt - dyPt, 0, maxY);
              return {
                ...f,
                id: crypto.randomUUID(),
                pageIndex: currentPage,
                xPt: nx,
                yPt: ny,
                selected: true,
              };
            });

      return rest.concat(finalNewOnes);
    });
  }, [currentPage]);

  // Enable keyboard deletion, copy, paste of selected fields via Delete/Backspace and Cmd/Ctrl+C/V
  useEffect(() => {
    const onKeyDown = (e: KeyboardEvent) => {
      // Prevent keyboard shortcuts from interfering with text input fields
      const isTypingTarget = (el: EventTarget | null) => {
        const node = el as HTMLElement | null;
        if (!node) return false;
        const tag = (node.tagName || "").toLowerCase();
        if (tag === "input" || tag === "textarea" || tag === "select")
          return true;
        if ((node as HTMLElement).isContentEditable) return true;
        const role = node.getAttribute?.("role");
        if (role === "textbox" || role === "spinbutton") return true;
        return false;
      };

      if (isTypingTarget(e.target)) {
        return; // let the input handle the keystroke (prevents deleting fields while editing text)
      }

      const anySelected = fields.some((f) => f.selected);

      // Delete or Backspace: remove selected fields
      if ((e.key === "Delete" || e.key === "Backspace") && anySelected) {
        e.preventDefault();
        deleteSelected();
        return;
      }

      // Cmd/Ctrl+C: copy selected fields (current page)
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === "c") {
        if (anySelected) {
          e.preventDefault();
          copySelected();
        }
        return;
      }

      // Cmd/Ctrl+V: paste clipboard (duplicates and selects the new ones)
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === "v") {
        e.preventDefault();
        pasteClipboard();
        return;
      }
    };

    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, [fields, copySelected, pasteClipboard]);

  const buildConfigPayload = useCallback(
    () => ({
      sourcePdfBaseName: fileName || "document",
      exportedAt: new Date().toISOString(),
      zoomAtExport: zoom,
      fields: fields.map(({ selected, ...rest }) => rest),
    }),
    [fields, fileName, zoom]
  );

  /** Export fields as JSON using canonical point geometry **/
  const exportPdf = async () => {
    try {
      // Prepare a clean, serializable view of the fields (omit transient UI state)
      const payload = buildConfigPayload();

      const json = JSON.stringify(payload, null, 2);
      const blob = new Blob([json], { type: "application/json" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `${fileName || "document"}-fields.json`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    } catch (e) {
      console.error(e);
      alert("Failed to export fields JSON. See console for details.");
    }
  };

  const saveConfig = async () => {
    if (!form?.id) return;
    try {
      const payload = buildConfigPayload();
      await updateForm({
        id: form.id,
        body: { config: JSON.stringify(payload) },
      }).unwrap();
    } catch (e) {
      console.error("Failed to save form config", e);
      alert("Failed to save form config. See console for details.");
    }
  };

  const pageFields = fields.filter((f) => f.pageIndex === currentPage);

  // Memoized selected field and helpers for geometry/name editing
  const selectedField = useMemo(
    () => fields.find((f) => f.selected) || null,
    [fields]
  );
  const nameInputRef = useRef<HTMLInputElement | null>(null);

  const selectedFields = useMemo(
    () => fields.filter((f) => f.selected),
    [fields]
  );

  const selectedExcludeState = useMemo(() => {
    const total = selectedFields.length;
    const excludedCount = selectedFields.filter(
      (f) => f.excludeFromResync
    ).length;
    return {
      allExcluded: total > 0 && excludedCount === total,
      indeterminate: excludedCount > 0 && excludedCount < total,
    };
  }, [selectedFields]);

  const updateManySelected = useCallback((patch: Partial<Field>) => {
    setFields((prev) =>
      prev.map((ff) => (ff.selected ? { ...ff, ...patch } : ff))
    );
  }, []);

  const updateManySelectedGeometry = useCallback(
    (next: { xPt?: number; yPt?: number; wPt?: number; hPt?: number }) => {
      setFields((prev) =>
        prev.map((ff) => {
          if (!ff.selected) return ff;
          const vp = viewportMap.current[ff.pageIndex];
          let xPt = next.xPt ?? ff.xPt;
          let yPt = next.yPt ?? ff.yPt;
          let wPt = next.wPt ?? ff.wPt;
          let hPt = next.hPt ?? ff.hPt;
          wPt = Math.max(1, wPt);
          hPt = Math.max(1, hPt);
          if (vp) {
            const maxX = vp.widthPt - wPt;
            const maxY = vp.heightPt - hPt;
            xPt = clamp(xPt, 0, maxX);
            yPt = clamp(yPt, 0, maxY);
          }
          return { ...ff, xPt, yPt, wPt, hPt };
        })
      );
    },
    []
  );

  const updateFieldById = useCallback((id: string, patch: Partial<Field>) => {
    setFields((prev) =>
      prev.map((ff) => (ff.id === id ? { ...ff, ...patch } : ff))
    );
  }, []);

  const updateSelectedGeometry = useCallback(
    (
      id: string,
      next: { xPt?: number; yPt?: number; wPt?: number; hPt?: number }
    ) => {
      const f = fields.find((ff) => ff.id === id);
      if (!f) return;
      const vp = viewportMap.current[f.pageIndex];
      let { xPt, yPt, wPt, hPt } = { ...f, ...next } as Field;
      // Ensure positive sizes and keep inside page bounds when possible
      wPt = Math.max(1, wPt);
      hPt = Math.max(1, hPt);
      if (vp) {
        const maxX = vp.widthPt - wPt;
        const maxY = vp.heightPt - hPt;
        xPt = clamp(xPt, 0, maxX);
        yPt = clamp(yPt, 0, maxY);
      }
      updateFieldById(id, { xPt, yPt, wPt, hPt });
    },
    [fields, updateFieldById]
  );

  const alignSelectedHorizontal = useCallback(
    (mode: "left" | "center" | "right") => {
      setFields((prev) => {
        const selected = prev.filter((f) => f.selected);
        if (selected.length < 2) return prev;
        const minX = Math.min(...selected.map((f) => f.xPt));
        const maxRight = Math.max(...selected.map((f) => f.xPt + f.wPt));
        const targetCenter = (minX + maxRight) / 2;

        return prev.map((ff) => {
          if (!ff.selected) return ff;
          let xPt = ff.xPt;
          if (mode === "left") xPt = minX;
          if (mode === "center") xPt = targetCenter - ff.wPt / 2;
          if (mode === "right") xPt = maxRight - ff.wPt;
          const vp = viewportMap.current[ff.pageIndex];
          if (vp) {
            const maxX = vp.widthPt - ff.wPt;
            xPt = clamp(xPt, 0, maxX);
          }
          return { ...ff, xPt };
        });
      });
    },
    []
  );

  const alignSelectedVertical = useCallback(
    (mode: "top" | "middle" | "bottom") => {
      setFields((prev) => {
        const selected = prev.filter((f) => f.selected);
        if (selected.length < 2) return prev;
        const minY = Math.min(...selected.map((f) => f.yPt));
        const maxTop = Math.max(...selected.map((f) => f.yPt + f.hPt));
        const targetCenter = (minY + maxTop) / 2;

        return prev.map((ff) => {
          if (!ff.selected) return ff;
          let yPt = ff.yPt;
          if (mode === "bottom") yPt = minY;
          if (mode === "middle") yPt = targetCenter - ff.hPt / 2;
          if (mode === "top") yPt = maxTop - ff.hPt;
          const vp = viewportMap.current[ff.pageIndex];
          if (vp) {
            const maxY = vp.heightPt - ff.hPt;
            yPt = clamp(yPt, 0, maxY);
          }
          return { ...ff, yPt };
        });
      });
    },
    []
  );

  useEffect(() => {
    if (selectedField && lastCreatedId && selectedField.id === lastCreatedId) {
      nameInputRef.current?.focus();
      nameInputRef.current?.select();
      setLastCreatedId(null);
    }
  }, [lastCreatedId, selectedField]);

  const moveField = useCallback((id: string, direction: "up" | "down") => {
    setFields((prev) => {
      const idx = prev.findIndex((f) => f.id === id);
      if (idx === -1) return prev;
      const target = direction === "up" ? idx - 1 : idx + 1;
      if (target < 0 || target >= prev.length) return prev;
      const next = [...prev];
      [next[idx], next[target]] = [next[target], next[idx]];
      return next;
    });
  }, []);

  const focusField = useCallback((id: string) => {
    setFields((prev) => prev.map((ff) => ({ ...ff, selected: ff.id === id })));
  }, []);

  useEffect(() => {
    if (!isFieldListOpen) return;
    const selected = fields.find((f) => f.selected);
    if (!selected) return;
    const node = listItemRefs.current[selected.id];
    if (node) {
      node.scrollIntoView({ block: "center", behavior: "smooth" });
    }
  }, [fields, isFieldListOpen]);

  return (
    <Box sx={{ display: "flex", flex: 1 }}>
      <Box
        sx={{
          height: "100%",
          display: "flex",
          flexDirection: "column",
          flex: 1,
        }}
      >
        <AppBar
          position="sticky"
          color="default"
          elevation={1}
          sx={{ top: 96, zIndex: (theme) => theme.zIndex.appBar }}
        >
          <Sheet
            sx={{
              gap: 1,
              display: "flex",
              alignItems: "center",
              p: 1,
              justifyContent: "center",
            }}
          >
            <input
              id="json-file-input"
              type="file"
              accept="application/json"
              style={{ display: "none" }}
              onChange={(e) => {
                const file = e.target.files?.[0];
                if (file) importJson(file);
                // reset the input so the same file can be re-imported if needed
                e.currentTarget.value = "";
              }}
            />
            <label htmlFor="json-file-input">
              <Tooltip title="Import fields from JSON">
                <span>
                  <Button
                    variant="soft"
                    startDecorator={<FileOpenIcon />}
                    component="span"
                  >
                    Import JSON
                  </Button>
                </span>
              </Tooltip>
            </label>
            <Box sx={{ flexGrow: 1 }} />
            <Button
              variant="soft"
              color="primary"
              onClick={() => setFieldListOpen(true)}
            >
              View Field List
            </Button>
            <Tooltip title="Draw textfield (drag to create)">
              <IconButton
                color={mode === "draw" ? "primary" : "neutral"}
                variant={mode === "draw" ? "solid" : "soft"}
                onClick={() => setMode((e) => (e !== "draw" ? "draw" : "pan"))}
              >
                <i className="fa-light fa-plus"></i>
              </IconButton>
            </Tooltip>
            <Tooltip title="Select / Edit">
              <IconButton
                color={mode === "edit" ? "primary" : "neutral"}
                variant={mode === "edit" ? "solid" : "soft"}
                onClick={() => setMode((e) => (e !== "edit" ? "edit" : "pan"))}
              >
                <i className="fa-light fa-hand-pointer"></i>
              </IconButton>
            </Tooltip>
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                gap: 0.5,
                ml: 2,
                width: 220,
              }}
            >
              <i className="fa-regular fa-magnifying-glass-minus"></i>
              <Slider
                size="small"
                value={zoom}
                step={0.1}
                min={0.5}
                max={2.5}
                onChange={(_, v) => setZoom(v as number)}
              />
              <i className="fa-regular fa-magnifying-glass-plus"></i>
            </Box>
            <Tooltip title="Duplicate selected">
              <span>
                <IconButton
                  disabled={!fields.some((f) => f.selected)}
                  onClick={duplicateSelected}
                >
                  <i className="fa fa-copy" />
                </IconButton>
              </span>
            </Tooltip>
            <Tooltip title="Delete selected">
              <span>
                <IconButton
                  disabled={!fields.some((f) => f.selected)}
                  onClick={deleteSelected}
                >
                  <i className="fa fa-trash" />
                </IconButton>
              </span>
            </Tooltip>

            <Tooltip title="Download fields as JSON">
              <span>
                <Button
                  variant="soft"
                  color="neutral"
                  startDecorator={<i className="fa fa-download" />}
                  onClick={exportPdf}
                  disabled={!pdfBytes || loading}
                >
                  Export JSON
                </Button>
              </span>
            </Tooltip>
            <Tooltip title="Save config to backend">
              <span>
                <Button
                  variant="solid"
                  color="primary"
                  startDecorator={<i className="fa fa-save" />}
                  onClick={saveConfig}
                  disabled={!form || isSaving}
                  loading={isSaving}
                >
                  Save
                </Button>
              </span>
            </Tooltip>
          </Sheet>
        </AppBar>

        <Box
          ref={containerRef}
          sx={{
            flex: 1,
            overflow: "auto",
            display: "grid",
            placeItems: "start center",
            p: 2,
          }}
        >
          <Box
            sx={{
              position: "relative",
              userSelect: mode === "pan" ? "text" : "none",
            }}
          >
            <canvas ref={canvasRef} style={{ display: "block" }} />
            {/* Overlay for interactive fields */}
            <div
              ref={overlayRef}
              onMouseDown={(e) => {
                const isBackground = e.currentTarget === e.target;
                if (mode === "draw") {
                  beginDraw(e);
                } else if (isBackground) {
                  beginMarquee(e);
                }
              }}
              onMouseMove={(e) => {
                onDrawMove(e);
                onDragMove(e);
                onResizeMove(e);
                onMarqueeMove(e);
              }}
              onMouseUp={() => {
                endDraw();
                endDrag();
                endResize();
                endMarquee();
              }}
              style={{
                position: "absolute",
                left: 0,
                top: 0,
                width: overlayStyle.width,
                height: overlayStyle.height,
                cursor:
                  mode === "draw"
                    ? "crosshair"
                    : mode === "edit"
                    ? "default"
                    : "grab",
              }}
            >
              {pageFields.map((f) => {
                const view = fromPdfPoints(
                  f.pageIndex,
                  f.xPt,
                  f.yPt,
                  f.wPt,
                  f.hPt
                );
                return (
                  <React.Fragment key={f.id}>
                    <div
                      onMouseDown={(e) => {
                        if (mode === "pan") return;
                        const isToggle = e.metaKey || e.ctrlKey;
                        setFields((prev) => {
                          if (isToggle) {
                            return prev.map((ff) =>
                              ff.id === f.id
                                ? { ...ff, selected: !ff.selected }
                                : ff
                            );
                          }
                          return prev.map((ff) => ({
                            ...ff,
                            selected: ff.id === f.id,
                          }));
                        });
                      }}
                      style={{
                        position: "absolute",
                        left: view.x,
                        top: view.y,
                        width: view.w,
                        height: view.h,
                        boxSizing: "border-box",
                        borderRadius: 4,
                        border: f.selected
                          ? "2px solid #1976d2"
                          : "1px solid rgba(0,0,0,0.25)",
                        background: "#e1f5fe",
                        display: "flex",
                        alignItems: "stretch",
                        zIndex: 2,
                        cursor: mode === "edit" ? "pointer" : undefined,
                      }}
                    >
                      <div
                        onMouseDown={(e) => startDrag(e, f.id)}
                        style={{
                          position: "absolute",
                          inset: 0,
                          cursor: mode === "pan" ? "default" : "move",
                        }}
                      />
                      <div
                        style={{
                          position: "relative",
                          fontSize: 8,
                          padding: 4,
                          width: "100%",
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "space-between",
                          pointerEvents: "none",
                          overflow: "hidden",
                        }}
                      >
                        <span style={{ opacity: 0.7 }}>
                          {f.name} ({f.group})<br />
                          {Math.round(f.xPt)}:{Math.round(f.yPt)} -{" "}
                          {Math.round(f.wPt)}x{Math.round(f.hPt)}
                        </span>
                      </div>
                    </div>
                    {f.selected && (
                      <>
                        <Handle
                          x={view.x + view.w}
                          y={view.y + view.h}
                          onMouseDown={(e) => startResize(e, f.id, "se")}
                        />
                        <Handle
                          x={view.x}
                          y={view.y}
                          onMouseDown={(e) => startResize(e, f.id, "nw")}
                        />
                      </>
                    )}
                  </React.Fragment>
                );
              })}

              {/* Drawing rectangle preview */}
              {drawingRect && (
                <div
                  style={{
                    position: "absolute",
                    left: drawingRect.x,
                    top: drawingRect.y,
                    width: drawingRect.w,
                    height: drawingRect.h,
                    border: "2px dashed #1976d2",
                    background: "rgba(25,118,210,0.08)",
                    pointerEvents: "none",
                    zIndex: 1,
                  }}
                />
              )}
              {selectionRect && (
                <div
                  style={{
                    position: "absolute",
                    left: selectionRect.x,
                    top: selectionRect.y,
                    width: selectionRect.w,
                    height: selectionRect.h,
                    border: "1px dashed #1976d2",
                    background: "rgba(25,118,210,0.08)",
                    pointerEvents: "none",
                    zIndex: 1,
                  }}
                />
              )}
            </div>
          </Box>

          {!pdfDocProxy && (
            <Box
              sx={{
                mt: 6,
                display: "grid",
                placeItems: "center",
                gap: 2,
              }}
            >
              {isFormLoading ? (
                <>
                  <CircularProgress />
                  <Typography variant="h6">Loading form…</Typography>
                </>
              ) : form?.url ? (
                <>
                  <CircularProgress />
                  <Typography variant="h6">Loading PDF…</Typography>
                </>
              ) : (
                <Typography variant="h6">
                  No PDF available for this form.
                </Typography>
              )}
            </Box>
          )}

          {loading && (
            <Box
              sx={{
                position: "absolute",
                inset: 0,
                display: "grid",
                placeItems: "center",
                pointerEvents: "none",
              }}
            >
              <CircularProgress />
            </Box>
          )}
        </Box>
      </Box>
      <Divider orientation="vertical" />
      <Box
        sx={{
          width: 320,
          height: "calc(100vh - 100px)",
          p: 2,
          display: "flex",
          position: "sticky",
          top: 92,
          flexDirection: "column",
          gap: 2,
        }}
      >
        <Typography variant="h6">Field Properties</Typography>
        <Divider />
        {selectedFields.length > 1 ? (
          <>
            <Typography variant="subtitle2" sx={{ opacity: 0.8 }}>
              Editing {selectedFields.length} selected fields
            </Typography>
            <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
              <Typography variant="body2" sx={{ opacity: 0.8 }}>
                Align
              </Typography>
              <Box sx={{ display: "flex", gap: 0.5, flexWrap: "wrap" }}>
                <Tooltip title="Align left">
                  <span>
                    <IconButton
                      size="sm"
                      variant="soft"
                      onClick={() => alignSelectedHorizontal("left")}
                    >
                      <AlignHorizontalLeftIcon fontSize="small" />
                    </IconButton>
                  </span>
                </Tooltip>
                <Tooltip title="Align center">
                  <span>
                    <IconButton
                      size="sm"
                      variant="soft"
                      onClick={() => alignSelectedHorizontal("center")}
                    >
                      <AlignHorizontalCenterIcon fontSize="small" />
                    </IconButton>
                  </span>
                </Tooltip>
                <Tooltip title="Align right">
                  <span>
                    <IconButton
                      size="sm"
                      variant="soft"
                      onClick={() => alignSelectedHorizontal("right")}
                    >
                      <AlignHorizontalRightIcon fontSize="small" />
                    </IconButton>
                  </span>
                </Tooltip>
                <Tooltip title="Align top">
                  <span>
                    <IconButton
                      size="sm"
                      variant="soft"
                      onClick={() => alignSelectedVertical("top")}
                    >
                      <VerticalAlignTopIcon fontSize="small" />
                    </IconButton>
                  </span>
                </Tooltip>
                <Tooltip title="Align middle">
                  <span>
                    <IconButton
                      size="sm"
                      variant="soft"
                      onClick={() => alignSelectedVertical("middle")}
                    >
                      <VerticalAlignCenterIcon fontSize="small" />
                    </IconButton>
                  </span>
                </Tooltip>
                <Tooltip title="Align bottom">
                  <span>
                    <IconButton
                      size="sm"
                      variant="soft"
                      onClick={() => alignSelectedVertical("bottom")}
                    >
                      <VerticalAlignBottomIcon fontSize="small" />
                    </IconButton>
                  </span>
                </Tooltip>
              </Box>
            </Box>
            <TextField
              label="Group (applies to all)"
              value={(() => {
                // If all selected share the same group, show it, else empty placeholder
                const groups = new Set(
                  selectedFields.map((f) => (f.group ?? "").trim())
                );
                return groups.size === 1 ? [...groups][0] : "";
              })()}
              onChange={(e) => updateManySelected({ group: e.target.value })}
              size="small"
              fullWidth
            />
            <FormControlLabel
              control={
                <Checkbox
                  size="small"
                  checked={selectedExcludeState.allExcluded}
                  indeterminate={selectedExcludeState.indeterminate}
                  onChange={(e) =>
                    updateManySelected({ excludeFromResync: e.target.checked })
                  }
                />
              }
              label="Exclude from resync"
            />
            <TextField
              select
              label="Text Align (applies to all)"
              value={(() => {
                const vals = new Set(selectedFields.map((f) => f.textAlign));
                return vals.size === 1 ? [...vals][0] : "";
              })()}
              onChange={(e) =>
                updateManySelected({
                  textAlign: e.target.value as "left" | "center" | "right",
                })
              }
              size="small"
              fullWidth
            >
              <MenuItem value="left">Left</MenuItem>
              <MenuItem value="center">Center</MenuItem>
              <MenuItem value="right">Right</MenuItem>
            </TextField>
            <FormControl component="fieldset" size="small">
              <FormLabel component="legend">Type (applies to all)</FormLabel>
              <RadioGroup
                row
                value={(() => {
                  const vals = new Set(selectedFields.map((f) => f.type));
                  return vals.size === 1 ? [...vals][0] : "";
                })()}
                onChange={(e) =>
                  updateManySelected({
                    type: e.target.value as Field["type"],
                  })
                }
              >
                <FormControlLabel
                  value="text"
                  control={<Radio />}
                  label="Text"
                />
                <FormControlLabel
                  value="date"
                  control={<Radio />}
                  label="Date"
                />
                <FormControlLabel
                  value="checkbox"
                  control={<Radio />}
                  label="Checkbox"
                />
              </RadioGroup>
            </FormControl>
            <TextField
              label="X (pt) — set for all"
              type="number"
              inputProps={{ step: 1, min: 0 }}
              onChange={(e) =>
                updateManySelectedGeometry({ xPt: Number(e.target.value) })
              }
              size="small"
              fullWidth
            />
            <TextField
              label="Y (pt) — set for all"
              type="number"
              inputProps={{ step: 1, min: 0 }}
              onChange={(e) =>
                updateManySelectedGeometry({ yPt: Number(e.target.value) })
              }
              size="small"
              fullWidth
            />
            <TextField
              label="Width (pt) — set for all"
              type="number"
              inputProps={{ step: 1, min: 1 }}
              onChange={(e) =>
                updateManySelectedGeometry({ wPt: Number(e.target.value) })
              }
              size="small"
              fullWidth
            />
            <TextField
              label="Height (pt) — set for all"
              type="number"
              inputProps={{ step: 1, min: 1 }}
              onChange={(e) =>
                updateManySelectedGeometry({ hPt: Number(e.target.value) })
              }
              size="small"
              fullWidth
            />
          </>
        ) : selectedField ? (
          <>
            <TextField
              label="Name"
              value={selectedField.name}
              inputRef={nameInputRef}
              onChange={(e) =>
                updateFieldById(selectedField.id, { name: e.target.value })
              }
              size="small"
              fullWidth
            />
            <TextField
              label="Group"
              value={selectedField.group ?? ""}
              onChange={(e) =>
                updateFieldById(selectedField.id, { group: e.target.value })
              }
              size="small"
              fullWidth
            />
            <FormControlLabel
              control={
                <Checkbox
                  size="small"
                  checked={selectedField.excludeFromResync}
                  onChange={(e) =>
                    updateFieldById(selectedField.id, {
                      excludeFromResync: e.target.checked,
                    })
                  }
                />
              }
              label="Exclude from resync"
            />
            <FormControl component="fieldset" size="small">
              <FormLabel component="legend">Type</FormLabel>
              <RadioGroup
                row
                value={selectedField.type}
                onChange={(e) =>
                  updateFieldById(selectedField.id, {
                    type: e.target.value as Field["type"],
                  })
                }
              >
                <FormControlLabel
                  value="text"
                  control={<Radio />}
                  label="Text"
                />
                <FormControlLabel
                  value="date"
                  control={<Radio />}
                  label="Date"
                />
                <FormControlLabel
                  value="checkbox"
                  control={<Radio />}
                  label="Checkbox"
                />
              </RadioGroup>
            </FormControl>
            <TextField
              select
              label="Text Align"
              value={selectedField.textAlign}
              onChange={(e) =>
                updateFieldById(selectedField.id, {
                  textAlign: e.target.value as "left" | "center" | "right",
                })
              }
              size="small"
              fullWidth
            >
              <MenuItem value="left">Left</MenuItem>
              <MenuItem value="center">Center</MenuItem>
              <MenuItem value="right">Right</MenuItem>
            </TextField>
            <TextField
              label="X (pt)"
              type="number"
              inputProps={{ step: 1, min: 0 }}
              value={Math.round(selectedField.xPt)}
              onChange={(e) =>
                updateSelectedGeometry(selectedField.id, {
                  xPt: Number(e.target.value),
                })
              }
              size="small"
              fullWidth
            />
            <TextField
              label="Y (pt)"
              type="number"
              inputProps={{ step: 1, min: 0 }}
              value={Math.round(selectedField.yPt)}
              onChange={(e) =>
                updateSelectedGeometry(selectedField.id, {
                  yPt: Number(e.target.value),
                })
              }
              size="small"
              fullWidth
            />
            <TextField
              label="Width (pt)"
              type="number"
              inputProps={{ step: 1, min: 1 }}
              value={Math.round(selectedField.wPt)}
              onChange={(e) =>
                updateSelectedGeometry(selectedField.id, {
                  wPt: Number(e.target.value),
                })
              }
              size="small"
              fullWidth
            />
            <TextField
              label="Height (pt)"
              type="number"
              inputProps={{ step: 1, min: 1 }}
              value={Math.round(selectedField.hPt)}
              onChange={(e) =>
                updateSelectedGeometry(selectedField.id, {
                  hPt: Number(e.target.value),
                })
              }
              size="small"
              fullWidth
            />
          </>
        ) : (
          <Typography variant="body2" sx={{ opacity: 0.7 }}>
            Select one or more fields to edit their properties.
          </Typography>
        )}
      </Box>
      <Divider orientation="vertical" />
      {isFieldListOpen ? (
        <Box
          sx={{
            width: 320,
            height: "calc(100vh - 100px)",
            overflow: "auto",
            p: 2,
            display: "flex",
            position: "sticky",
            top: 92,
            flexDirection: "column",
            gap: 2,
          }}
        >
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              gap: 1,
              position: "sticky",
              top: -16,
              bgcolor: "background.body",
              zIndex: 1,
              py: 1,
            }}
          >
            <Typography variant="h6">Field List</Typography>
            <Button
              size="sm"
              variant="outlined"
              color="neutral"
              onClick={() => setFieldListOpen(false)}
            >
              Close
            </Button>
          </Box>
          <Divider />
          {fields.length ? (
            <List dense>
              {fields.map((field, index) => (
                <ListItem
                  key={field.id}
                  divider
                  disableGutters
                  selected={field.selected}
                  onClick={() => focusField(field.id)}
                  ref={(el: any) => {
                    listItemRefs.current[field.id] = el;
                  }}
                  sx={{
                    borderRadius: 1,
                    "&.Mui-selected": {
                      bgcolor: "primary.softBg",
                    },
                    "&.Mui-selected:hover": {
                      bgcolor: "primary.softBg",
                    },
                  }}
                >
                  <ListItemText
                    primary={field.name || "Untitled"}
                    secondary={
                      <>
                        <Typography variant="caption" component="div">
                          UUID: {field.id}
                        </Typography>
                        <Typography variant="caption" component="div">
                          Group: {field.group || "—"}
                        </Typography>
                      </>
                    }
                  />
                  <Box sx={{ display: "flex", gap: 0.5, ml: 1 }}>
                    <IconButton
                      size="sm"
                      variant="outlined"
                      color="neutral"
                      onClick={() => moveField(field.id, "up")}
                      disabled={index === 0}
                    >
                      <ArrowUpwardIcon fontSize="small" />
                    </IconButton>
                    <IconButton
                      size="sm"
                      variant="outlined"
                      color="neutral"
                      onClick={() => moveField(field.id, "down")}
                      disabled={index === fields.length - 1}
                    >
                      <ArrowDownwardIcon fontSize="small" />
                    </IconButton>
                  </Box>
                </ListItem>
              ))}
            </List>
          ) : (
            <Typography variant="body2" sx={{ opacity: 0.7 }}>
              No fields yet. Create a field to see it listed here.
            </Typography>
          )}
        </Box>
      ) : (
        []
      )}
    </Box>
  );
};

export default PdfTextfieldAnnotator;

/**
 * Usage notes:
 * - Install deps: `npm i @mui/material @emotion/react @emotion/styled pdf-lib pdfjs-dist`
 * - Vite/Webpack: the `?url` import for the worker is one approach; adapt if needed.
 * - This component is self-contained; render it anywhere in your app.
 * - Geometry is stored in PDF points; fields will now stick across zoom levels.
 */
